import { memo, useEffect, useState, useMemo, useContext, useCallback, type FunctionComponent, type ReactNode } from 'react';
import map from 'lodash/map';
import get from 'lodash/get';
import size from 'lodash/size';
import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';
import toLower from 'lodash/toLower';
import { useLazyQuery, useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
// Material Icon imports
import RestoreIcon from '@mui/icons-material/Restore';
import ClearAllIcon from '@mui/icons-material/ClearAll';
// Skillmore UI Components
import { getFullName } from '@empathco/ui-components/src/helpers/strings';
import { bold } from '@empathco/ui-components/src/helpers/intl';
import useQueryObject from '@empathco/ui-components/src/hooks/useQueryObject';
import useMutationMethod from '@empathco/ui-components/src/hooks/useMutationMethod';
import useConfirmationDialog from '@empathco/ui-components/src/hooks/useConfirmationDialog';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import CloseIconButton from '@empathco/ui-components/src/elements/CloseIconButton';
import CardTitle from '@empathco/ui-components/src/elements/CardTitle';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
import ActionSucceededMessage from '@empathco/ui-components/src/elements/ActionSucceededMessage';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
import ConfirmDialog from '@empathco/ui-components/src/elements/ConfirmDialog';
// local imports
import { ADMIN_EMPLOYEE_QUERY } from '../graphql/AdminEmployee';
import { RESET_CONFIRMED_SKILLS } from '../graphql/ResetConfirmedSkills';
import { UPDATE_ADMIN_EMPLOYEE } from '../graphql/UpdateAdminEmployee';
import {
  AdminEmployee, AdminEmployeeDocument, AdminJob, ResetConfirmedSkillsDocument, UpdateAdminEmployeeDocument
} from '../graphql/types';
import { TalentEmployeeObject } from '../graphql/customTypes';
import { SKILL_LEVEL_MAX, Skill } from '../models/skill';
import { Job } from '../models/job';
import { GlobalContext } from '../context/global';
import ImpersonateButton from '../elements/ImpersonateButton';
import EmployeeTalentBand from '../v3/EmployeeTalentBand';
import JobDetailsItem from '../v3/JobDetailsItem';
import SkillsGrid from '../v3/SkillsGrid';
// SCSS imports
import { lightBg } from './AdminEmployeeDialog.module.scss';

const cardTitleSx: SxProps<Theme> = {
  mr: { xs: 3, sm: 0 },
  flexWrap: 'wrap !important',
  '& .MuiCardHeader-action': {
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    rowGap: 2,
    columnGap: 2,
    my: 1,
    mr: { xs: 0, sm: 5 }
  }
} as const;

type AdminEmployeeDialogProps = {
  readonly employee?: AdminEmployee | null;
  readonly onJobClick?: (code: string, job?: Job | AdminJob, context?: TalentEmployeeObject | AdminEmployee) => void;
  readonly onClose: () => void;
  // for Strorybook only
  readonly testPending?: number;
}

const AdminEmployeeDialog: FunctionComponent<AdminEmployeeDialogProps> = ({
  employee: parentEmployee,
  onJobClick,
  onClose,
  testPending
  // eslint-disable-next-line complexity, max-statements, max-lines-per-function
}) => {
  const { paths: { supvEmplPath } } = useContext(GlobalContext);

  const { query: getEmployee, pending: pendingEmployee, failed, results: employee } = useQueryObject({
    data: undefined as unknown as AdminEmployee,
    key: 'adminEmployee',
    flatResults: true,
    lazyQuery: useLazyQuery(ADMIN_EMPLOYEE_QUERY as typeof AdminEmployeeDocument)
  });
  const {
    mutate: updateEmployee, loading: updatePending, failed: updateFailed, succeeded: updateSucceeded
  } = useMutationMethod({
    // TODO: key: 'updateAdminEmployee', -- restore when backend responds with `success: true`
    mutation: useMutation(UPDATE_ADMIN_EMPLOYEE as typeof UpdateAdminEmployeeDocument)
  });
  const {
    mutate: resetSkills, loading: resetPending, failed: resetFailed, succeeded: resetSucceeded
  } = useMutationMethod({
    // TODO: key: 'resetConfirmedSkills', -- restore when backend responds with `success: true`
    mutation: useMutation(RESET_CONFIRMED_SKILLS as typeof ResetConfirmedSkillsDocument)
  });

  const pending = pendingEmployee || !employee;

  const { id, code: parentCode, first_name: parentFirstName, last_name: parentLastName } = parentEmployee || {};
  const parentTitle = getFullName(parentFirstName, parentLastName, parentCode);
  const {
    code, first_name, last_name, management_level, org, org_unit, email, skills,
    is_contractor, is_countable, is_esau_untouchable, is_inference_target, by_sso, profile
  } = employee || {};
  const emplCode = code || parentCode;
  const title = getFullName(first_name, last_name, code);
  const skillsCount = size(skills);

  const startedProfileBuilder = useMemo(() => {
    try {
      const profileObj = profile ? JSON.parse(profile) : null;
      return Boolean(profileObj) && size(get(profileObj, 'onboarding_steps')) >= 1;
    } catch (_err) {
      return false;
    }
  }, [profile]);

  useEffect(() => {
    if (id) getEmployee?.({ variables: { employee_id: id } });
  }, [id, getEmployee]);

  const isOpen = Boolean(parentEmployee);

  const [mounted, setMounted] = useState(isOpen);
  useEffect(() => {
    if (isOpen) setMounted(true);
  }, [isOpen]);

  const transitionProps = useMemo(() => ({ onExited: () => {
    setMounted(false);
  } }), []);

  const resetProfileBuilder = useCallback(() => id ? updateEmployee?.({
    variables: { employee_id: id, input: { profile_reset: true } },
    update: (cache) => cache.evict({ id: 'ROOT_QUERY', fieldName: 'adminEmployee' })
  }) : undefined, [id, updateEmployee]);

  const resetConfirmedSkills = useCallback(() => id ? resetSkills?.({
    variables: { employee_id: id, input: {} },
    update: (cache) => cache.evict({ id: 'ROOT_QUERY', fieldName: 'adminEmployee' })
  }) : undefined, [id, resetSkills]);

  const {
    confirmOpen: confirmPBOpen,
    confirmMounted: confirmPBMounted,
    confirmPending: confirmPBPending,
    handleAction: handleProfileBuilderReset,
    handleCancel: handlePBCancel,
    handleExited: handlePBExited,
    handleConfirm: handlePBConfirm
  } = useConfirmationDialog(resetProfileBuilder, { holdWhilePending: true });

  const {
    confirmOpen: confirmCSOpen,
    confirmMounted: confirmCSMounted,
    confirmPending: confirmCSPending,
    handleAction: handleConfirmedSkillsReset,
    handleCancel: handleCSCancel,
    handleExited: handleCSExited,
    handleConfirm: handleCSConfirm
  } = useConfirmationDialog(resetConfirmedSkills, { holdWhilePending: true });

  const msgValues = useMemo(() => ({ br: <br/>, bold, name: parentTitle }), [parentTitle]);

  const disabled = pending || updatePending || resetPending ? true : undefined;

  const requiresSkills = useMemo(() => sortBy(skills,
    ({ title: skillTitle, expected_level }) => `${SKILL_LEVEL_MAX - (expected_level || 0)}${skillTitle}`), [skills]);

  const details = useMemo(() => [
    ...isNil(management_level) ? [] : [{ label: 'role.admin.management_level', value: management_level }],
    ...isNil(is_contractor) ? [] : [{ label: 'role.admin.is_contractor', value: is_contractor }],
    ...isNil(email) ? [] : [{ label: 'role.admin.email', value: email }],
    ...isNil(org) ? [] : [{ label: 'role.admin.org', value: org.title }],
    ...isNil(org_unit) ? [] : [{ label: 'role.admin.org_unit', value: org_unit.title }],
    ...skillsCount < 1 ? [{ label: 'role.admin.skills', value: 0 }] : []
  ] as { label: string; value: number | string; }[], [skillsCount, management_level, is_contractor, email, org, org_unit]);

  const extra = useMemo(() =>
    [
      ...isNil(is_countable) ? [] : [{ label: 'role.admin.is_countable', value: is_countable }],
      ...isNil(is_esau_untouchable) ? [] : [{ label: 'role.admin.is_esau_untouchable', value: is_esau_untouchable }],
      ...isNil(is_inference_target) ? [] : [{ label: 'role.admin.is_inference_target', value: is_inference_target }],
      ...isNil(by_sso) ? [] : [{ label: 'role.admin.by_sso', value: by_sso }]
    ] as { label: string; value: number | string | ReactNode; }[],
    [is_countable, is_esau_untouchable, is_inference_target, by_sso]
  );

  useEffect(() => {
    if (testPending === 2) updateEmployee?.({ variables: { employee_id: 321, input: { profile_reset: true } } });
  }, [testPending, updateEmployee]);

  useEffect(() => {
    if (testPending === 3) resetSkills?.({ variables: { employee_id: 321, input: {} } });
  }, [testPending, resetSkills]);

  const bands = [details, extra].map((band, index) => band.length >= 1 ? (
    <>
      {index === 1 ? <Divider/> : undefined}
      <CardSection
          compact flex
          dark={index === 0 ? true : undefined}
          className={index === 1 ? lightBg : undefined}
      >
        <Box
            py={2.5}
            flexGrow={1}
            display="flex"
            alignItems="center"
            flexWrap="wrap"
        >
          {map(band, ({ label, value }, idx) => (
            <JobDetailsItem
                key={label}
                prompt={<FormattedMessage id={label}/>}
                value={value}
                divider={idx < band.length - 1}
            />
          ))}
        </Box>
      </CardSection>
      {index === 0 ? <Divider/> : undefined}
    </>
  ) : undefined);

  return mounted ? (
    <>
      <Dialog
          disableEnforceFocus
          maxWidth="xl"
          fullWidth
          scroll="body"
          open={isOpen}
          onClose={disabled ? undefined : onClose}
          TransitionProps={transitionProps}
      >
        <CloseIconButton onClick={onClose}/>
        <CardTitle
            title={(failed && (parentTitle || 'admin.employees.default_name')) ||
              (pending && (parentTitle || <Skeleton variant="text" width="12rem"/>)) ||
              title || 'admin.employees.default_name'}
            subheader={emplCode ? <ImpersonateButton large code={emplCode} disabled={pending}/> : undefined}
            withDivider
            sx={cardTitleSx}
            action={(
              <>
                {window.ADMIN_CAN_RESET === '1' || toLower(window.ADMIN_CAN_RESET || '') === 'true' ? (
                  <Button
                      type="submit"
                      color="warning"
                      variant="outlined"
                      disableElevation
                      startIcon={resetPending ? <CircularProgress size={18} color="inherit"/> : <ClearAllIcon/>}
                      disabled={disabled}
                      onClick={handleConfirmedSkillsReset}
                  >
                    <FormattedMessage id="admin.employees.reset_confirmed_skills"/>
                  </Button>
                ) : undefined}
                <Button
                    type="submit"
                    color="error"
                    variant="outlined"
                    disableElevation
                    startIcon={updatePending ? <CircularProgress size={18} color="inherit"/> : <RestoreIcon/>}
                    disabled={disabled || !startedProfileBuilder}
                    onClick={handleProfileBuilderReset}
                >
                  <FormattedMessage id="admin.employees.reset_profile_builder"/>
                </Button>
              </>
            )}
        />
        {(failed && <FetchFailedAlert flat/>) || (pending && <LoadingPlaceholder flat/>) || (mounted ? (
          <>
            <CardSection compact>
              <EmployeeTalentBand employee={employee} route={supvEmplPath} onJobClick={onJobClick} withoutDivider/>
            </CardSection>
            <Divider/>
            {bands[0]}
            {skillsCount >= 1 ? (
              <>
                <CardSection top>
                  <BoxTypography pt={1} variant="subtitle1">
                    <FormattedMessage id="role.admin.skills"/>
                  </BoxTypography>
                </CardSection>
                <SkillsGrid
                    plain
                    readOnly
                    manualOnly
                    withLink={false}
                    skills={(requiresSkills || []) as Skill[]}
                />
              </>
            ) : undefined}
            {bands[1]}
          </>
        ) : undefined)}
      </Dialog>
      {confirmPBMounted ? (
        <ConfirmDialog
            open={confirmPBOpen}
            title="admin.employees.reset_profile_builder"
            text="admin.employees.reset_profile_builder.confirm"
            withCancelButton
            values={msgValues}
            pending={confirmPBPending}
            onCancel={handlePBCancel}
            onConfirm={handlePBConfirm}
            onExited={handlePBExited}
        />
      ) : undefined}
      {confirmCSMounted ? (
        <ConfirmDialog
            open={confirmCSOpen}
            title="admin.employees.reset_confirmed_skills"
            text="admin.employees.reset_confirmed_skills.confirm"
            withCancelButton
            values={msgValues}
            pending={confirmCSPending}
            onCancel={handleCSCancel}
            onConfirm={handleCSConfirm}
            onExited={handleCSExited}
        />
      ) : undefined}
      <ActionSnackbar
          severity="error"
          message="admin.employees.reset_confirmed_skills.error"
          open={resetFailed}
      />
      <ActionSnackbar
          severity="error"
          message="admin.employees.reset_profile_builder.error"
          open={updateFailed}
      />
      <ActionSucceededMessage
          message="admin.employees.reset_confirmed_skills.success"
          open={resetSucceeded}
          values={msgValues}
      />
      <ActionSucceededMessage
          message="admin.employees.reset_profile_builder.success"
          open={updateSucceeded}
          values={msgValues}
      />
    </>
  ) : null;
};

export default memo(AdminEmployeeDialog);
